import React, { useState, useEffect } from 'react';
import { CSSProperties } from 'react';
import useMaestroTasks from 'Hooks/useMaestroTasks';
import useBrand from 'Hooks/useBrand';
import { Body3, Subtitle1, Body2 } from 'components/shareable/Typography';
import clsx from 'clsx';
import TagSymphony from 'elements/TagSymphony';
import ModalSymphony from 'elements/ModalSymphony';
import { styx } from 'modules/Utils';
import TaskPackPurchaseModal from 'components/TaskPackPurchaseModal/TaskPackPurchaseModal';
import { useAuth0 } from '@auth0/auth0-react';

interface MaestroTaskCounterProps {
    className?: string;
    showUpgradeHint?: boolean;
}

const MaestroTaskCounter: React.FC<MaestroTaskCounterProps> = ({
    className,
    showUpgradeHint = false
}) => {

    const { isAuthenticated } = useAuth0();
    const { taskUsage, isLoadingUsage,
        setTaskPackModalOpen,
        getTasksRemaining, getNeedsUpgrade, getCanConsumeTask, fetchTaskUsage } = useMaestroTasks();
    const tasksRemaining = getTasksRemaining();
    const needsUpgrade = getNeedsUpgrade();
    const canConsume = getCanConsumeTask();
    const { currentBrand, isPaidBrand } = useBrand();

    const [showActionExplanation, setShowActionExplanation] = useState(false);

    // Initialize task usage when brand becomes available
    useEffect(() => {
        if (currentBrand?.id && !taskUsage && !isLoadingUsage) {
            fetchTaskUsage(currentBrand.id);
        }
    }, [currentBrand?.id, taskUsage, isLoadingUsage]);

    // Refresh task usage when brand tier changes (after subscription upgrades)
    useEffect(() => {
        if (currentBrand?.id && currentBrand.tier) {
            fetchTaskUsage(currentBrand.id);
        }
    }, [currentBrand?.tier]); // Only depend on tier changes

    const handleTagClick = () => {
        if (tasksRemaining > 0 || !isAuthenticated) {
            setShowActionExplanation(true);
        } else {
            setTaskPackModalOpen(true);
        }
    };

    const renderTaskTag = (tasksRemaining: number) => {
        let displayText = '';
        let colorClass = '';

        if (needsUpgrade) {
            displayText = 'No AI Actions Left';
            colorClass = 'taskCounterError';
        } else if (tasksRemaining <= 5) {
            displayText = `${tasksRemaining} AI Action${tasksRemaining === 1 ? '' : 's'} Remaining`;
            colorClass = 'taskCounterWarning';
        } else {
            displayText = `${tasksRemaining} AI Actions Remaining`;
            colorClass = 'taskCounterNormal';
        }

        if (showUpgradeHint && needsUpgrade && !isPaidBrand) {
            displayText = 'Upgrade for more AI Actions';
        }
        return (
            <div
                className={clsx('maestroTaskCounter', className, colorClass)}
                style={sty.container}
            >
                <TagSymphony
                    className="taskCounterTag"
                    style={styx(sty.tag, sty.clickableTag)}
                    color={colorClass}
                    title={displayText}
                    onClick={handleTagClick}
                />
            </div>
        )
    }

    const renderActionExplanation = () => {
        return (
            <ModalSymphony
                title="What is an AI Action?"
                subtitle="Maestro can perform marketing actions on your behalf. Each action is a single task."
                className="actionExplanation"
                isOpen={showActionExplanation}
                onClose={() => setShowActionExplanation(false)}
            >
                <div className="actionExplanationContent" style={sty.modalContent}>
                    <Body2 style={sty.explanationText}>
                        AI Actions are tasks that Maestro performs automatically to set up and optimize your marketing. Each action costs 1 task from your monthly allocation.
                    </Body2>

                    <div className="actionTypesList">
                        <Body3 style={{ fontWeight: 600, marginBottom: 8 }}>Actions include:</Body3>
                        <ul style={{ margin: 0, paddingLeft: 16, listStyleType: 'disc' }}>
                            <li>Creating and launching ad campaigns</li>
                            <li>Generating marketing strategies</li>
                            <li>Analyzing your content + giving ideas</li>
                        </ul>
                    </div>
                    <Body2 style={{ marginTop: 16, fontWeight: 500 }}>
                        Your monthly action allocation varies by plan. Upgrade to get more actions and unlock Maestro's full potential.
                    </Body2>
                    <div className="actionTypesList">
                        <Body3 style={{ fontWeight: 600, marginBottom: 8 }}>In the future, Maestro will be able to:</Body3>
                        <ul style={{ margin: 0, paddingLeft: 16, listStyleType: 'disc' }}>
                            <li>Build landing pages & pre-saves</li>
                            <li>Generate video + image content</li>
                            <li>Create ad creatives</li>
                            <li>Automate more advanced marketing tasks</li>
                            <li>And more!</li>
                        </ul>
                    </div>

                </div>
            </ModalSymphony>
        )
    }

    // Show loading state instead of null
    if (isLoadingUsage) {
        return (
            <div
                className={clsx('maestroTaskCounter', 'maestroTaskCounterLoading', className)}
                style={sty.container}
            >
                <Body3
                    className="taskCounterText"
                    style={sty.text}
                >
                    Loading tasks...
                </Body3>
            </div>
        );
    }

    // Show fallback if no data yet (only if we have a brand but no task data)
    // if (!taskUsage) {
    //     return (
    //         <div
    //             className={clsx('maestroTaskCounter', 'maestroTaskCounterFallback', className)}
    //             style={sty.container}
    //         >
    //             <Body3
    //                 className="taskCounterText"
    //                 style={sty.text}
    //             >
    //                 {currentBrand?.id ? 'Loading task info...' : '1 AI Action Remaining'}
    //             </Body3>
    //         </div>
    //     );
    // }


    let tasksToShow = isAuthenticated ? tasksRemaining : 1;
    return (
        <>
            {renderTaskTag(tasksToShow)}
            {renderActionExplanation()}
        </>
    )
};

const sty: Record<string, CSSProperties> = {
    container: {
        display: 'flex',
        alignItems: 'center',
        marginLeft: 'auto',
        marginRight: 4
    },
    text: {
        fontSize: 12,
        fontWeight: 500,
        color: '#333',
    },
    tag: {
        // Base tag styles
    },
    clickableTag: {
        cursor: 'pointer',
        transition: 'opacity 0.2s ease',
    },
    modalContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        width: '100%',
    },
    explanationText: {
        lineHeight: '1.5',
        marginBottom: 0,
    }
};

export default MaestroTaskCounter; 